/**
 * AuthenticatedLayout Component
 *
 * Wrapper layout for authenticated pages.
 * Contains Header with user info and logout, Sidebar for navigation,
 * and main content area for children.
 *
 * Responsive: sidebar collapses on mobile with hamburger menu.
 * Desktop: sidebar can be collapsed/expanded with toggle button.
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { UserMenu } from './UserMenu';
import { useAuth } from '../../contexts/AuthContext';

export interface AuthenticatedLayoutProps {
  children: React.ReactNode;
}

export const AuthenticatedLayout: React.FC<AuthenticatedLayoutProps> = ({
  children,
}) => {
  const { user } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isLargeScreen, setIsLargeScreen] = useState(false);

  // Track screen size for responsive behavior
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 1024px)');

    const handleChange = (e: MediaQueryListEvent | MediaQueryList) => {
      setIsLargeScreen(e.matches);
      // On mobile, default to closed; on desktop, default to open
      if (!e.matches) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    // Set initial value
    handleChange(mediaQuery);

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  if (!user) {
    return null;
  }

  // Calculate margin based on sidebar state
  const sidebarWidth = sidebarOpen ? '260px' : '64px';

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--color-bg)',
      }}
    >
      {/* Sidebar */}
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
        isLargeScreen={isLargeScreen}
      />

      {/* Main content wrapper */}
      <div
        style={{
          marginLeft: isLargeScreen ? sidebarWidth : 0,
          transition: 'margin-left var(--transition-base)',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Top header */}
        <header
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 30,
            height: '60px',
            backgroundColor: 'color-mix(in srgb, var(--bg) 82%, transparent)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderBottom: '1px solid var(--line)',
            display: 'flex',
            alignItems: 'center',
            padding: '0 var(--space-6)',
            justifyContent: 'space-between',
          }}
        >
          {/* Left side: hamburger menu (mobile only) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
            {/* Mobile menu button */}
            {!isLargeScreen && (
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                style={{
                  padding: 'var(--space-2)',
                  width: 36,
                  height: 36,
                  backgroundColor: 'transparent',
                  border: '1px solid var(--line-strong)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--ink-soft)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                aria-label="Open menu"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
            )}

            {/* Mobile logo */}
            {!isLargeScreen && (
              <Link
                to="/"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  textDecoration: 'none',
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    backgroundColor: 'var(--ink)',
                    color: 'var(--bg)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'var(--font-body)',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    letterSpacing: '-0.03em',
                    borderRadius: 'var(--radius-sm)',
                    position: 'relative',
                  }}
                >
                  W
                  <span
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      inset: 'auto 3px 3px auto',
                      width: 5,
                      height: 5,
                      borderRadius: '50%',
                      background: 'var(--accent)',
                    }}
                  />
                </div>
              </Link>
            )}
          </div>

          {/* Right side: user menu */}
          <UserMenu />
        </header>

        {/* Main content area */}
        <main
          style={{
            flex: 1,
            padding: 'var(--space-8) var(--space-8)',
          }}
        >
          {children}
        </main>

        {/* Footer */}
        <footer
          style={{
            borderTop: '1px solid var(--line)',
            padding: 'var(--space-4) var(--space-8)',
            backgroundColor: 'var(--bg)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 'var(--space-4)',
            }}
          >
            <p
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '0.7rem',
                color: 'var(--ink-muted)',
                margin: 0,
                letterSpacing: '0.05em',
              }}
            >
              WebGen Engine · GDPR-Compliant Website Generator
            </p>
            <p
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: '0.7rem',
                color: 'var(--ink-muted)',
                margin: 0,
                letterSpacing: '0.05em',
              }}
            >
              © {new Date().getFullYear()} All rights reserved
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default AuthenticatedLayout;
